import type { FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import { Plus } from 'lucide-react'

import { Reveal } from '@/components/generals/reveal'
import { Container, Eyebrow } from '@/components/generals/primitives'

interface FaqBlockProps {
    faqs: Faq[]
    eyebrow?: string
    title?: string
    className?: string
}

/* Native disclosure elements: keyboard and screen reader behaviour comes for
   free and the answers are in the HTML whether scripting runs or not. */
export const FaqBlock: FC<FaqBlockProps> = ({
    faqs,
    eyebrow = 'Common questions',
    title = 'The things people ask before they commit',
    className
}): ReactNode => {
    if (!faqs.length) return null

    return (
        <section id='faq' className={className ?? 'border-t border-rule bg-paper py-20 lg:py-28'}>
            <Container>
                <div className='grid gap-12 lg:grid-cols-12 lg:gap-16'>
                    <div className='lg:col-span-4'>
                        <Reveal>
                            <Eyebrow>{eyebrow}</Eyebrow>

                            <h2 className='mt-6 font-heading text-3xl font-extrabold tracking-display text-ink sm:text-4xl'>
                                {title}
                            </h2>
                        </Reveal>
                    </div>

                    <div className='lg:col-span-7 lg:col-start-6'>
                        {faqs.map((faq, index) => (
                            <Reveal key={faq.id} delay={index * 45}>
                                <details className='group border-t border-rule last:border-b'>
                                    <summary className='flex cursor-pointer list-none items-start justify-between gap-6 py-6 [&::-webkit-details-marker]:hidden'>
                                        <h3
                                            data-mdlfy-cms={`faqs:${faq.id}:question`}
                                            className='font-heading text-lg font-bold tracking-title text-ink'
                                        >
                                            {faq.question}
                                        </h3>

                                        <Plus
                                            aria-hidden='true'
                                            className='mt-1 size-5 shrink-0 text-minium transition-transform duration-300 ease-out group-open:rotate-45'
                                        />
                                    </summary>

                                    <p
                                        data-mdlfy-cms={`faqs:${faq.id}:answer`}
                                        className='max-w-2xl pb-7 text-[0.95rem] leading-relaxed text-muted-foreground'
                                    >
                                        {faq.answer}
                                    </p>
                                </details>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </Container>
        </section>
    )
}
